/**
 * Parent and child rows of a split in the transactions table (SEE-1602, built with
 * SEE-1604). After Submit all splits the original transaction is an inert parent and
 * each split is a child row. The search returns them as ordinary rows in sort order;
 * this groups each parent with the children on the same page and works out what each
 * row shows:
 *
 *   parent  - charge facts and the total; coding, status, receipt and note columns
 *             blank (Figma closed/expanded parent); no click, no selection.
 *   child   - "Split n of m", own amount and coding; the inherited charge facts (card,
 *             cardholder, date) blank while its parent is on the page, shown again
 *             when it is not (search, or a filter that lifted it out), so it is never
 *             orphaned.
 */

import type { HighnoteTransactionEvent } from 'API';

/** The split linkage the search payload carries (SEE-1821); absent on the Amplify type. */
export interface SplitLinkage {
  isSplitParent?: boolean | null;
  splitChildIds?: Array<string | null> | null;
  splitParentId?: string | null;
  splitId?: string | null;
}

export type SplitAwareTransaction = HighnoteTransactionEvent & SplitLinkage;

export interface SplitRowMeta {
  role: 'parent' | 'child';
  /** 1-based position among the parent's children (child only). */
  index: number;
  /** How many children the split has. */
  count: number;
  /** The parent row is on the same page (child only). */
  parentPresent: boolean;
}

export type SplitRow<T extends SplitAwareTransaction = SplitAwareTransaction> = T & { _split?: SplitRowMeta };

export const isSplitParent = (tx: SplitLinkage | null | undefined): boolean => !!tx?.isSplitParent;
export const isSplitChild = (tx: SplitLinkage | null | undefined): boolean => !!tx?.splitParentId;

/** The child's position in its parent's list; the list order is creation order. */
function childIndex(parent: SplitLinkage | undefined, child: SplitAwareTransaction): number {
  const ids = parent?.splitChildIds ?? [];
  const at = ids.indexOf(child.id);
  if (at >= 0) return at + 1;
  // No parent on the page: the id carries the position (te_split_<parentId>_<n>).
  const tail = child.id.split('_').pop();
  const n = Number(tail);
  return Number.isInteger(n) && n > 0 ? n : 1;
}

/**
 * Reorder a page so each parent is followed by its children, and tag every split
 * row with what it should show. Rows that are not part of a split are untouched.
 */
export function groupSplitRows<T extends SplitAwareTransaction>(rows: T[]): SplitRow<T>[] {
  const parents = new Map<string, T>();
  const childrenByParent = new Map<string, T[]>();
  for (const row of rows) {
    if (isSplitParent(row)) parents.set(row.id, row);
    if (isSplitChild(row)) {
      const siblings = childrenByParent.get(row.splitParentId as string) ?? [];
      siblings.push(row);
      childrenByParent.set(row.splitParentId as string, siblings);
    }
  }
  if (parents.size === 0 && childrenByParent.size === 0) return rows;

  const out: SplitRow<T>[] = [];
  const placed = new Set<string>();
  for (const row of rows) {
    if (placed.has(row.id)) continue;
    if (isSplitParent(row)) {
      const children = [...(childrenByParent.get(row.id) ?? [])].sort(
        (a, b) => childIndex(row, a) - childIndex(row, b)
      );
      const count = row.splitChildIds?.length || children.length;
      out.push({ ...row, _split: { role: 'parent', index: 0, count, parentPresent: true } });
      placed.add(row.id);
      for (const child of children) {
        out.push({
          ...child,
          _split: {
            role: 'child',
            index: childIndex(row, child),
            count,
            parentPresent: true,
          },
        });
        placed.add(child.id);
      }
      continue;
    }
    if (isSplitChild(row)) {
      const parent = parents.get(row.splitParentId as string);
      if (parent) continue; // emitted under its parent above or below
      // One count for the whole orphaned group, so children 1 and 3 on a filtered page
      // read "of 3" alike instead of each guessing from its own position.
      const siblings = childrenByParent.get(row.splitParentId as string) ?? [];
      const siblingCount = Math.max(siblings.length, ...siblings.map((s) => childIndex(undefined, s)));
      out.push({
        ...row,
        _split: {
          role: 'child',
          index: childIndex(undefined, row),
          count: siblingCount,
          parentPresent: false,
        },
      });
      placed.add(row.id);
      continue;
    }
    out.push(row);
    placed.add(row.id);
  }
  return out;
}

/**
 * The rows a view that must not show the split structure gets (SEE-1602, group admin
 * table per the Figma): parents dropped, children left as ordinary rows in place.
 */
export function withoutSplitParents<T extends SplitAwareTransaction>(rows: T[]): T[] {
  return rows.some(isSplitParent) ? rows.filter((r) => !isSplitParent(r)) : rows;
}

/**
 * The row a link or URL id opens. Review links carry the Highnote transactionId, which
 * every child of a split shares with its parent, so a plain match could open the parent:
 * an inert container that must never be coded or submitted. An exact row id wins;
 * otherwise the first non-parent row with that transactionId, and a parent hit falls
 * through to its first child on the page.
 */
export function resolveLinkedTransaction<T extends SplitAwareTransaction>(
  rows: T[],
  id: string | undefined
): T | undefined {
  if (!id) return undefined;
  const exact = rows.find((t) => t.id === id);
  if (exact && !isSplitParent(exact)) return exact;
  const byTransaction = rows.filter((t) => t.transactionId === id || t.id === id);
  const nonParent = byTransaction.find((t) => !isSplitParent(t));
  if (nonParent) return nonParent;
  const parent = byTransaction.find(isSplitParent);
  if (!parent) return undefined;
  const firstChildId = (parent.splitChildIds ?? []).find((childId) => rows.some((t) => t.id === childId));
  return firstChildId
    ? rows.find((t) => t.id === firstChildId)
    : rows.find((t) => t.splitParentId === parent.id);
}

/** The split meta on a table row, if it is part of a split. */
export const splitMetaOf = (row: unknown): SplitRowMeta | undefined =>
  (row as { _split?: SplitRowMeta } | null | undefined)?._split;

/** Columns a parent never shows: it is never coded, has no note and no receipt of its own. */
export const PARENT_BLANK_COLUMNS = new Set(['receipt', 'note', 'transactionType', 'status']);
/** Columns a child inherits from its parent and leaves blank while the parent is on the page. */
export const CHILD_INHERITED_COLUMNS = new Set(['card', 'cardholder', 'date']);
